import React, { Fragment, useEffect, useState } from "react";
import { NavLink, Route } from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import Image from "react-bootstrap/Image";
import Dropdown from "react-bootstrap/Dropdown";
import Fade from "react-bootstrap/Fade";

import NavLinkComponent from "../NavLink";
import LinkComponent from "../Link";
import useWidthHook from "../../Hooks/useWidth";
import ChevronIcon from "../../Icons/Chevron";
import CloseIcon from "../../Icons/Close";
import ArrowIcon from "../../Icons/Arrow";
import LogoIcon from "../../Icons/Logo";
import MenuIcon from "../../Icons/Menu";

import LayoutProfileDropdown from "./Navbar/ProfileDropdown";
// import LayoutNotification from "./Navbar/Notification";

const propTypes = {
  show: PropTypes.bool,
  back: PropTypes.func,
  app: PropTypes.object,
  profileDropdown: PropTypes.any,
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      link: PropTypes.string,
      icon: PropTypes.any,
      exact: PropTypes.bool,
      cta: PropTypes.any,
      subNavItems: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          link: PropTypes.string,
          exact: PropTypes.bool
        })
      )
    })
  )
};

const Template = ({ app, show, back, children, navItems, profileDropdown }) => {
  /**
   * state
   */
  const [toggle, setToggle] = useState(false);
  const [hasSubNav, setHasSubNav] = useState(true);

  /**
   * hooks
   */
  const width = useWidthHook();

  /**
   * effect
   */
  useEffect(() => {
    if (width >= 1200) {
      setToggle(false);
    }
  }, [width]);

  return (
    <Fragment>
      {show ? (
        <Wrapper $subnav={hasSubNav}>
          <NavWrapper
            $toggle={toggle}
            $subnav={hasSubNav}
            aria-label="Side nav"
          >
            <Nav>
              <div className="nav__app mb-6">
                <Dropdown>
                  <Dropdown.Toggle
                    aria-label="Company Dropdown"
                    className="p-2 justify-content-center gap-1 w-100"
                  >
                    <Image
                      alt="Company logo"
                      style={{ width: "2rem", height: "2rem" }}
                      className="img-fit rounded-circle nav__app__logo"
                      src={
                        app?.logo ||
                        process.env.REACT_APP_PLACEHOLDER_LOGO_IMAGE
                      }
                    />
                    {Boolean(app?.dropdown) && (
                      <ChevronIcon size={16} direction="down" />
                    )}
                  </Dropdown.Toggle>
                  {Boolean(app?.dropdown) && app.dropdown}
                </Dropdown>
              </div>

              <div className="nav__items">
                {navItems.map((navItem, key) => (
                  <Fragment key={key}>
                    {!navItem.hide && (
                      <NavLinkComponent
                        to={navItem.link}
                        exact={navItem?.exact}
                        className="nav__items__item"
                        isActive={(props) => {
                          if (props && props?.path) {
                            if (
                              props.path.replace(/\\/g, "") === navItem.link
                            ) {
                              setTimeout(() => {
                                setHasSubNav(!!navItem.subNavItems?.length);
                              });
                            }
                          }
                        }}
                      >
                        {(match) => (
                          <Fragment>
                            <navItem.icon
                              className="mb-2"
                              {...(!match && { variant: "outline" })}
                            />
                            <small className="text-truncate">
                              {navItem.name}
                            </small>
                          </Fragment>
                        )}
                      </NavLinkComponent>
                    )}
                  </Fragment>
                ))}
              </div>
            </Nav>
            <SubNav className={!hasSubNav ? "d-none" : ""}>
              {navItems.map(({ link, name, exact, subNavItems, cta }, key) => (
                <Fragment key={key}>
                  {subNavItems?.length && (
                    <Route path={link} {...{ exact }}>
                      <div className="px-5 py-6 mb-6 d-flex align-items-center justify-content-between gap-2">
                        <p className="text-truncate">{name}</p>
                        {width < 1200 && (
                          <LinkComponent onClick={() => setToggle(false)}>
                            <CloseIcon />
                          </LinkComponent>
                        )}
                      </div>
                      <div className="sub-nav__items mb-6">
                        {subNavItems?.map((item, key) => (
                          <NavLink
                            onClick={() => setToggle(false)}
                            className="sub-nav__items__link"
                            exact={item?.exact}
                            to={item.link}
                            key={key}
                          >
                            <span>{item.name}</span>
                            {item.badge && (
                              <span
                                style={{
                                  lineHeight: 1.5,
                                  padding: "2px 4px"
                                }}
                                className="d-inline-block text-gray bg-gray-10 rounded caption fw-bold"
                              >
                                {item.badge}
                              </span>
                            )}
                          </NavLink>
                        ))}
                      </div>

                      {cta && <div className="px-5">{cta}</div>}

                      <LogoIcon
                        className="mx-auto mt-auto mb-6"
                        color="var(--bs-gray-40)"
                      />
                    </Route>
                  )}
                </Fragment>
              ))}
            </SubNav>
          </NavWrapper>

          <Main>
            <TopNav className="mx-n4 mx-md-n8 mx-xl-n10 px-4 px-md-8 px-xl-10">
              {back ? (
                <div className="mx-n4">
                  <LinkComponent className="p-4" onClick={back}>
                    <ArrowIcon direction="left" />
                  </LinkComponent>
                </div>
              ) : (
                <Fragment>
                  {width < 1200 && (
                    <div className="mx-n4">
                      <LinkComponent
                        className="p-4"
                        onClick={() => setToggle(true)}
                      >
                        <MenuIcon />
                      </LinkComponent>
                    </div>
                  )}
                </Fragment>
              )}

              <div className="items">
                {/* <LayoutNotification /> */}
                <LayoutProfileDropdown {...profileDropdown} />
              </div>
            </TopNav>
            <main>{children}</main>
          </Main>
        </Wrapper>
      ) : (
        children
      )}

      {/* sidenav backdrop */}
      <Fade
        in={toggle}
        mountOnEnter
        unmountOnExit
        onClick={() => setToggle(!toggle)}
      >
        <div className="modal-backdrop" style={{ zIndex: 900 }} />
      </Fade>
    </Fragment>
  );
};

/**
 * styles
 */
const TopNav = styled.nav`
  top: 0;
  z-index: 100;
  display: flex;
  height: 4.5rem;
  position: sticky;
  align-items: center;
  margin-bottom: 0.5rem;
  background-color: #fff;

  .items {
    gap: 1.5rem;
    display: flex;
    margin-left: auto;
    align-items: center;
  }
`;

const Main = styled.div`
  height: 100%;
  padding: 0 1rem;
  overflow-y: auto;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }

  @media (min-width: 1200px) {
    padding: 0 2.5rem;
  }
`;

const SubNav = styled.div`
  height: 100%;
  display: flex;
  background-color: #fff;
  flex-direction: column;

  .sub-nav__items__link {
    justify-content: space-between;
    padding: 0.875rem 1.25rem;
    align-items: center;
    font-size: 0.875rem;
    display: flex;
    color: #000;

    &:not(:last-child) {
      margin-bottom: 0.5rem;
    }

    &.active {
      background-color: var(--bs-gray-20);
      font-weight: 600;
    }
  }
`;

const Nav = styled.div`
  height: 100%;
  background-color: var(--bs-gray-10);

  .nav__app {
    padding: 0.75rem 0.25rem;

    .dropdown-toggle {
      height: 3rem;
      border-radius: 0.25rem;

      &:focus,
      &:active {
        background-color: var(--bs-gray-20);
      }
    }
  }

  .nav__items {
    &__item {
      color: #000;
      display: block;
      text-align: center;
      padding: 0.75rem 0.5rem;

      small {
        display: block;
        font-size: 0.625rem;
      }

      &:not(:last-child) {
        margin-bottom: 1rem;
      }

      &.active {
        background-color: var(--bs-gray-40);

        small {
          font-weight: 600;
        }
      }
    }
  }
`;

const NavWrapper = styled.nav`
  top: 0;
  height: 100%;
  z-index: 910;
  display: grid;
  position: fixed;
  background-color: #fff;
  transition: ease-in-out left 0.15s;
  border-right: solid 1px var(--bs-gray-20);
  grid-template-columns: ${({ $subnav }) =>
    $subnav ? "5rem 14.5rem" : "5rem"};
  left: ${({ $toggle, $subnav }) =>
    $toggle ? "0" : $subnav ? "-19.5rem" : "-5rem"};
  width: ${({ $subnav }) =>
    $subnav ? "calc(19.5rem + 1px)" : "calc(5rem + 1px)"};

  @media (min-width: 1200px) {
    left: 0;
    position: inherit;
  }
`;

const Wrapper = styled.div`
  height: 100vh;

  @media (min-width: 1200px) {
    display: grid;
    grid-template-columns: ${({ $subnav }) =>
        $subnav ? "calc(19.5rem + 1px)" : "calc(5rem + 1px)"} minmax(0, 1fr);
  }
`;

Template.propTypes = propTypes;

export default Template;
